/**
 * PipelineMatrixPage — Panel 2 de Originacion
 * Vertical blocks per level, semanas en renglones.
 * Driver: la oferta.
 * Supports interval grouping: weekly, biweekly, monthly, semiannual.
 */

import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { RefreshCw } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { ROUTES, PARTNER_ID } from '@/constants';
import { CarteraFunnelSection } from '@/features/credit/components/CarteraFunnel';

import { useQuery } from '@tanstack/react-query';
import { getPipelineMatrix } from '@/services/credit-engine.service';

// Types kept locally (match backend response)
interface PipelineCellData { count: number; amount_mxn: number; avg_mxn: number }
interface CreditStatusBreakdown { active_pct: number; paid_off_pct: number; delinquent_pct: number }
interface PipelineLevelRow { level: string; offers: PipelineCellData; applications: PipelineCellData; credits: PipelineCellData; conversion_pct: number; credit_status: CreditStatusBreakdown; avg_term_weeks: number; avg_annual_rate: number }
interface PipelineWeek { week: string; total: PipelineLevelRow; levels: PipelineLevelRow[] }

// ─── Constants ──────────────────────────────────────────────────────────────

const BREADCRUMBS = [
  { label: 'Inicio', path: ROUTES.DASHBOARD },
  { label: 'Originacion', path: ROUTES.PIPELINE_MATRIX },
  { label: 'Pipeline', path: ROUTES.PIPELINE_MATRIX },
];

const LEVEL_FULL: Record<string, string> = {
  TOTAL: 'Total', PLATINUM: 'Platino', GOLD: 'Oro', SILVER: 'Plata', BRONZE: 'Bronce',
};

const LEVEL_BORDER: Record<string, string> = {
  TOTAL: 'border-l-slate-600', PLATINUM: 'border-l-blue-500', GOLD: 'border-l-[#DAA520]', SILVER: 'border-l-[#94A3B8]', BRONZE: 'border-l-[#CD7F32]',
};

const LEVEL_HEADER: Record<string, string> = {
  TOTAL: 'bg-slate-200 text-slate-800 dark:bg-slate-700/60 dark:text-slate-300',
  PLATINUM: 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400',
  GOLD: 'bg-amber-100 text-amber-800 dark:bg-[#DAA520]/10 dark:text-[#DAA520]',
  SILVER: 'bg-gray-200 text-gray-700 dark:bg-[#94A3B8]/10 dark:text-[#94A3B8]',
  BRONZE: 'bg-orange-100 text-orange-800 dark:bg-[#CD7F32]/10 dark:text-[#CD7F32]',
};

const LEVEL_ROW_BG: Record<string, string> = {
  TOTAL: 'bg-slate-50 dark:bg-transparent',
  PLATINUM: 'bg-purple-50/50 dark:bg-transparent',
  GOLD: 'bg-amber-50/50 dark:bg-transparent',
  SILVER: 'bg-gray-50 dark:bg-transparent',
  BRONZE: 'bg-orange-50/50 dark:bg-transparent',
};

const ROW_H = 36;
const HEADER_H = 28;
const SUB_HEADER_H = 20;

const INTERVAL_OPTIONS = [
  { value: 'weekly', label: 'Semanal' },
  { value: 'monthly', label: 'Mensual' },
  { value: 'semiannual', label: 'Semestral' },
];

const ROWS_COUNT = 20;

function fk(v: number): string {
  if (v === 0) return '—';
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `$${(v / 1_000).toFixed(0)}k`;
  return `$${v}`;
}

// ─── Interval Grouping ──────────────────────────────────────────────────────

function getYearWeek(week: string): { year: number; w: number } {
  const parts = week.split('-W');
  return { year: parseInt(parts[0] ?? '2026', 10), w: parseInt(parts[1] ?? '0', 10) };
}

function groupKey(week: string, interval: string): string {
  const { year, w } = getYearWeek(week);
  switch (interval) {
    case 'biweekly': {
      const bw = Math.ceil(w / 2);
      return `${year}-BW${String(bw).padStart(2, '0')}`;
    }
    case 'monthly': {
      const m = Math.ceil(w / 4.33);
      const months = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
      return `${months[Math.min(m - 1, 11)]} ${String(year).slice(2)}`;
    }
    case 'semiannual': {
      const half = w <= 26 ? 'H1' : 'H2';
      return `${year}-${half}`;
    }
    default:
      return week.replace(/^\d{4}-/, '');
  }
}

function mergeLevelRows(rows: PipelineLevelRow[]): PipelineLevelRow {
  const totalO = rows.reduce((s, r) => s + r.offers.count, 0);
  const totalOAmt = rows.reduce((s, r) => s + r.offers.amount_mxn, 0);
  const totalS = rows.reduce((s, r) => s + r.applications.count, 0);
  const totalSAmt = rows.reduce((s, r) => s + r.applications.amount_mxn, 0);
  const totalC = rows.reduce((s, r) => s + r.credits.count, 0);
  const totalCAmt = rows.reduce((s, r) => s + r.credits.amount_mxn, 0);
  const conv = totalO > 0 ? (totalC / totalO) * 100 : 0;

  // Weighted average of credit status
  const totalCredits = rows.reduce((s, r) => s + r.credits.count, 0);
  const active = totalCredits > 0 ? rows.reduce((s, r) => s + r.credit_status.active_pct * r.credits.count, 0) / totalCredits : 0;
  const paid = totalCredits > 0 ? rows.reduce((s, r) => s + r.credit_status.paid_off_pct * r.credits.count, 0) / totalCredits : 0;
  const del_ = totalCredits > 0 ? rows.reduce((s, r) => s + r.credit_status.delinquent_pct * r.credits.count, 0) / totalCredits : 0;

  const avgTerm = totalCredits > 0 ? rows.reduce((s, r) => s + r.avg_term_weeks * r.credits.count, 0) / totalCredits : 0;
  const avgRate = totalCredits > 0 ? rows.reduce((s, r) => s + r.avg_annual_rate * r.credits.count, 0) / totalCredits : 0;

  return {
    level: rows[0]?.level ?? 'TOTAL',
    offers: { count: totalO, amount_mxn: totalOAmt, avg_mxn: totalO > 0 ? totalOAmt / totalO : 0 },
    applications: { count: totalS, amount_mxn: totalSAmt, avg_mxn: totalS > 0 ? totalSAmt / totalS : 0 },
    credits: { count: totalC, amount_mxn: totalCAmt, avg_mxn: totalC > 0 ? totalCAmt / totalC : 0 },
    conversion_pct: conv,
    credit_status: { active_pct: active, paid_off_pct: paid, delinquent_pct: del_ },
    avg_term_weeks: avgTerm,
    avg_annual_rate: avgRate,
  };
}

function groupWeeks(weeks: PipelineWeek[], interval: string): { label: string; total: PipelineLevelRow; levels: PipelineLevelRow[] }[] {
  if (interval === 'weekly') {
    return weeks.map((w) => ({
      label: w.week.replace(/^\d{4}-/, ''),
      total: w.total,
      levels: w.levels,
    }));
  }

  const groups = new Map<string, PipelineWeek[]>();
  for (const w of weeks) {
    const key = groupKey(w.week, interval);
    const arr = groups.get(key) ?? [];
    arr.push(w);
    groups.set(key, arr);
  }

  return Array.from(groups.entries()).map(([label, wks]) => ({
    label,
    total: mergeLevelRows(wks.map((w) => w.total)),
    levels: ['PLATINUM', 'GOLD', 'SILVER', 'BRONZE'].map((level) =>
      mergeLevelRows(wks.map((w) => w.levels.find((l) => l.level === level)!).filter(Boolean))
    ),
  }));
}

// ─── Compute % of total per level ───────────────────────────────────────────

function computeLevelPct(weeks: { total: PipelineLevelRow; levels: PipelineLevelRow[] }[]): Record<string, number> {
  const totalOffers = weeks.reduce((s, w) => s + w.total.offers.count, 0);
  const result: Record<string, number> = {};
  const levels = ['PLATINUM', 'GOLD', 'SILVER', 'BRONZE'];
  for (const level of levels) {
    const levelOffers = weeks.reduce((s, w) => {
      const l = w.levels.find((lv) => lv.level === level);
      return s + (l?.offers.count ?? 0);
    }, 0);
    result[level] = totalOffers > 0 ? (levelOffers / totalOffers) * 100 : 0;
  }
  return result;
}

// ─── Status Bar ─────────────────────────────────────────────────────────────

function StatusBar({ status }: { status: CreditStatusBreakdown }) {
  const total = status.active_pct + status.paid_off_pct + status.delinquent_pct;
  if (total === 0) return <div className="h-1.5 w-full rounded-full bg-muted" />;
  return (
    <div className="flex h-1.5 w-full overflow-hidden rounded-full bg-muted">
      {status.active_pct > 0 && <div className="bg-green-500" style={{ width: `${status.active_pct}%` }} />}
      {status.paid_off_pct > 0 && <div className="bg-blue-400" style={{ width: `${status.paid_off_pct}%` }} />}
      {status.delinquent_pct > 0 && <div className="bg-red-500" style={{ width: `${status.delinquent_pct}%` }} />}
    </div>
  );
}

// ─── Cell ───────────────────────────────────────────────────────────────────

function Cell({ count, amount, isTotal, trend }: { count: number; amount: number; isTotal: boolean; trend?: 'up' | 'down' | 'same' | undefined }) {
  if (count === 0) return <div className="text-center text-[10px] text-muted-foreground/25">—</div>;
  const trendIcon = trend === 'up' ? '▲' : trend === 'down' ? '▼' : '';
  const trendColor = trend === 'up' ? 'text-green-500' : trend === 'down' ? 'text-red-500' : '';
  return (
    <div className="text-center leading-tight">
      <div className={isTotal ? 'text-base font-bold text-foreground' : 'text-[11px] font-medium text-foreground/80'}>
        {fk(amount)}
        {trendIcon && <span className={`text-[7px] ml-0.5 ${trendColor}`}>{trendIcon}</span>}
      </div>
      <div className="text-[8px] text-muted-foreground">{count}</div>
    </div>
  );
}

// ─── Week Column ────────────────────────────────────────────────────────────

function WeekColumn({ labels }: { labels: string[] }) {
  return (
    <div className="shrink-0" style={{ width: 52 }}>
      <div style={{ height: HEADER_H + 20 + SUB_HEADER_H }} />
      {labels.map((label, i) => (
        <div
          key={label}
          className={`flex items-center px-1 text-[10px] font-medium ${i === 0 ? 'text-foreground' : 'text-muted-foreground'}`}
          style={{ height: ROW_H }}
        >
          {label}
        </div>
      ))}
      <div
        className="flex items-center px-1 text-[10px] font-semibold text-foreground"
        style={{ height: ROW_H + 8 }}
      >
        Total
      </div>
    </div>
  );
}

// ─── Level Block ────────────────────────────────────────────────────────────

function LevelBlock({ level, rows, isTotal, pctOfTotal, onHeaderClick }: { level: string; rows: { label: string; data: PipelineLevelRow }[]; isTotal: boolean; pctOfTotal?: number | undefined; onHeaderClick: (stage: string) => void }) {
  const border = LEVEL_BORDER[level] ?? '';
  const header = LEVEL_HEADER[level] ?? '';
  const rowBg = LEVEL_ROW_BG[level] ?? '';
  const cols = 'grid-cols-[1fr_1fr_1fr_36px_32px_24px]';

  const headerBtn = 'cursor-pointer underline decoration-dotted underline-offset-2 hover:decoration-solid hover:opacity-80';

  // Compute averages for header
  const totalCreditsCount = rows.reduce((s, r) => s + r.data.credits.count, 0);
  const totalCreditsAmt = rows.reduce((s, r) => s + r.data.credits.amount_mxn, 0);
  const avgTicket = totalCreditsCount > 0 ? totalCreditsAmt / totalCreditsCount : 0;
  const avgTerm = totalCreditsCount > 0
    ? rows.reduce((s, r) => s + r.data.avg_term_weeks * r.data.credits.count, 0) / totalCreditsCount
    : 0;
  const avgRate = totalCreditsCount > 0
    ? rows.reduce((s, r) => s + r.data.avg_annual_rate * r.data.credits.count, 0) / totalCreditsCount
    : 0;

  return (
    <div className={`rounded-lg border border-border ${border} border-l-4 overflow-hidden ${isTotal ? 'flex-[2]' : 'flex-1'} min-w-0`}>
      {/* Level name + stats */}
      <div className={`${header} px-2 py-1.5`} style={{ minHeight: HEADER_H + 20 }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <span className={isTotal ? 'text-xs font-semibold' : 'text-[10px] font-semibold uppercase tracking-wider'}>
              {LEVEL_FULL[level] ?? level}
            </span>
            {pctOfTotal != null && (
              <span className="text-[9px] font-normal opacity-60">{pctOfTotal.toFixed(0)}%</span>
            )}
          </div>
          <div className="flex items-center gap-3 text-[10px]">
            <span className="opacity-50">Prom <span className="font-medium opacity-100">{fk(avgTicket)}</span></span>
            <span className="opacity-50">Plazo <span className="font-medium opacity-100">{avgTerm > 0 ? `${avgTerm.toFixed(0)}s` : '—'}</span></span>
            <span className="opacity-50">Tasa <span className="font-medium opacity-100">{avgRate > 0 ? `${(avgRate * 100).toFixed(0)}%` : '—'}</span></span>
          </div>
        </div>
      </div>

      {/* Sub-headers — clickable */}
      <div className={`grid ${cols} ${header} text-[8px] uppercase tracking-wider border-b border-border/50`} style={{ height: SUB_HEADER_H }}>
        <button type="button" className={`flex items-center justify-center font-medium ${headerBtn}`} onClick={() => onHeaderClick('offers')}>{isTotal ? 'Ofertas' : 'O'}</button>
        <button type="button" className={`flex items-center justify-center font-medium ${headerBtn}`} onClick={() => onHeaderClick('applications')}>{isTotal ? 'Solicitudes' : 'S'}</button>
        <button type="button" className={`flex items-center justify-center font-medium ${headerBtn}`} onClick={() => onHeaderClick('credits')}>{isTotal ? 'Créditos' : 'C'}</button>
        <div className="flex items-center justify-center font-medium">%</div>
        <div className="flex items-center justify-center font-medium">St</div>
        <div className="flex items-center justify-center font-medium">M</div>
      </div>

      {/* Rows */}
      {rows.map((row, i) => {
        const prev = i < rows.length - 1 ? rows[i + 1]?.data : null;

        const getTrend = (curr: number, prevVal: number | undefined): 'up' | 'down' | 'same' | undefined => {
          if (prevVal == null || prevVal === 0) return undefined;
          if (curr > prevVal) return 'up';
          if (curr < prevVal) return 'down';
          return 'same';
        };

        return (
          <div
            key={row.label}
            className={`grid ${cols} ${rowBg} border-b border-border/15 items-center`}
            style={{ height: ROW_H }}
          >
            <Cell count={row.data.offers.count} amount={row.data.offers.amount_mxn} isTotal={isTotal} trend={getTrend(row.data.offers.amount_mxn, prev?.offers.amount_mxn)} />
            <Cell count={row.data.applications.count} amount={row.data.applications.amount_mxn} isTotal={isTotal} trend={getTrend(row.data.applications.amount_mxn, prev?.applications.amount_mxn)} />
            <Cell count={row.data.credits.count} amount={row.data.credits.amount_mxn} isTotal={isTotal} trend={getTrend(row.data.credits.amount_mxn, prev?.credits.amount_mxn)} />
            <div className="flex items-center justify-center">
              <span className={`text-[9px] font-medium tabular-nums ${
                row.data.conversion_pct >= 50 ? 'text-green-600'
                  : row.data.conversion_pct >= 25 ? 'text-amber-600'
                    : row.data.conversion_pct > 0 ? 'text-red-600' : 'text-muted-foreground/25'
              }`}>
                {row.data.conversion_pct > 0 ? `${row.data.conversion_pct.toFixed(0)}%` : '—'}
              </span>
            </div>
            <div className="px-0.5 flex items-center">
              <StatusBar status={row.data.credit_status} />
            </div>
            <div className="flex items-center justify-center">
              {row.data.credit_status.delinquent_pct > 0 ? (
                <span className={`text-[8px] font-medium tabular-nums ${row.data.credit_status.delinquent_pct > 15 ? 'text-red-600' : row.data.credit_status.delinquent_pct > 5 ? 'text-amber-600' : 'text-green-600'}`}>
                  {row.data.credit_status.delinquent_pct.toFixed(0)}%
                </span>
              ) : (
                <span className="text-[8px] text-muted-foreground/25">—</span>
              )}
            </div>
          </div>
        );
      })}

      {/* Totals row */}
      {(() => {
        const totO = rows.reduce((s, r) => s + r.data.offers.count, 0);
        const totOAmt = rows.reduce((s, r) => s + r.data.offers.amount_mxn, 0);
        const totS = rows.reduce((s, r) => s + r.data.applications.count, 0);
        const totSAmt = rows.reduce((s, r) => s + r.data.applications.amount_mxn, 0);
        const totC = rows.reduce((s, r) => s + r.data.credits.count, 0);
        const totCAmt = rows.reduce((s, r) => s + r.data.credits.amount_mxn, 0);
        const totConv = totO > 0 ? (totC / totO) * 100 : 0;
        const totMora = totC > 0
          ? rows.reduce((s, r) => s + r.data.credit_status.delinquent_pct * r.data.credits.count, 0) / totC
          : 0;

        return (
          <div
            className={`grid ${cols} ${header} items-center border-t-2 border-border`}
            style={{ height: ROW_H + 8 }}
          >
            <Cell count={totO} amount={totOAmt} isTotal={true} />
            <Cell count={totS} amount={totSAmt} isTotal={true} />
            <Cell count={totC} amount={totCAmt} isTotal={true} />
            <div className="flex items-center justify-center">
              <span className={`text-[9px] font-medium tabular-nums ${totConv >= 50 ? 'text-green-700' : totConv >= 25 ? 'text-amber-700' : 'text-red-700'}`}>
                {totConv > 0 ? `${totConv.toFixed(0)}%` : '—'}
              </span>
            </div>
            <div />
            <div className="flex items-center justify-center">
              {totMora > 0 ? (
                <span className={`text-[8px] font-medium tabular-nums ${totMora > 15 ? 'text-red-700' : totMora > 5 ? 'text-amber-700' : 'text-green-700'}`}>
                  {totMora.toFixed(0)}%
                </span>
              ) : (
                <span className="text-[8px] text-muted-foreground/25">—</span>
              )}
            </div>
          </div>
        );
      })()}
    </div>
  );
}


// ─── Main Page ──────────────────────────────────────────────────────────────

export function PipelineMatrixPage() {
  const navigate = useNavigate();
  const [interval, setInterval] = useState('weekly');

  const { data: pipelineData } = useQuery({
    queryKey: ['pipeline-matrix'],
    queryFn: () => getPipelineMatrix(PARTNER_ID, 20),
  });
  const matrix: PipelineWeek[] = pipelineData?.data ?? [];

  // DEBUG: log raw pipeline data to diagnose missing levels
  if (matrix.length > 0 && matrix[0]) {
    const firstWeek = matrix[0]!
    console.log('[Pipeline] Total weeks:', matrix.length);
    console.log('[Pipeline] First week total:', JSON.stringify(firstWeek.total, null, 2));
    console.log('[Pipeline] First week levels:', JSON.stringify(firstWeek.levels, null, 2));
    console.log('[Pipeline] Levels count:', firstWeek.levels?.length);
    // Check if any level has applications/credits
    firstWeek.levels?.forEach((l: any) => {
      if (l.applications?.count > 0 || l.credits?.count > 0) {
        console.log(`[Pipeline] Level ${l.level} HAS data: apps=${l.applications?.count}, credits=${l.credits?.count}`);
      } else {
        console.log(`[Pipeline] Level ${l.level} EMPTY: offers=${l.offers?.count}, apps=${l.applications?.count}, credits=${l.credits?.count}`);
      }
    });
  }

  const grouped = useMemo(() => groupWeeks(matrix, interval), [matrix, interval]);
  const reversed = useMemo(() => [...grouped].reverse().slice(0, ROWS_COUNT), [grouped]);
  const levelPcts = useMemo(() => computeLevelPct(reversed), [reversed]);
  const labels = reversed.map((g) => g.label);
  const levels = ['PLATINUM', 'GOLD', 'SILVER', 'BRONZE'];

  const STAGE_ROUTES: Record<string, string> = {
    offers: '/credit/offers',
    applications: '/credit/applications',
    credits: '/credit/active',
  };

  const handleHeaderClick = (level: string, stage: string) => {
    const route = STAGE_ROUTES[stage];
    if (!route) return;
    if (level === 'TOTAL') {
      navigate(route);
    } else {
      navigate(`${route}?level=${level}`);
    }
  };

  return (
    <div>
      <PageHeader
        title="Pipeline por Semana" helpKey="pipeline-matrix"
        subtitle="Conversion de ofertas a creditos por nivel. Driver: la oferta."
        breadcrumbs={BREADCRUMBS}
        actions={
          <div className="flex items-center gap-2">
            
            <Button onClick={() => window.location.reload()}>
              <RefreshCw size={16} />
              Actualizar
            </Button>
          </div>
        }
      />

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex flex-wrap items-center gap-4 text-[10px] text-muted-foreground">
            <span><span className="font-medium text-foreground">%</span> Conv O→C</span>
            <span className="flex items-center gap-1"><span className="inline-block h-2 w-3 rounded-sm bg-green-500" /> Activo</span>
            <span className="flex items-center gap-1"><span className="inline-block h-2 w-3 rounded-sm bg-blue-400" /> Pagado</span>
            <span className="flex items-center gap-1"><span className="inline-block h-2 w-3 rounded-sm bg-red-500" /> Moroso</span>
            <span><span className="font-medium text-foreground">M</span> Morosidad</span>
          </div>
          <Select
            options={INTERVAL_OPTIONS}
            value={interval}
            onChange={setInterval}
            placeholder="Intervalo"
          />
        </div>

        <div className="flex gap-2">
          <WeekColumn labels={labels} />
          <LevelBlock
            level="TOTAL"
            rows={reversed.map((g) => ({ label: g.label, data: g.total }))}
            isTotal={true}
            onHeaderClick={(stage) => handleHeaderClick('TOTAL', stage)}
          />
          {levels.map((level) => (
            <LevelBlock
              key={level}
              level={level}
              rows={reversed.map((g) => ({
                label: g.label,
                data: g.levels.find((l) => l.level === level) ?? g.total,
              }))}
              isTotal={false}
              pctOfTotal={levelPcts[level]}
              onHeaderClick={(stage) => handleHeaderClick(level, stage)}
            />
          ))}
        </div>

        {/* Funnel agregado de cartera (suma de todas las trayectorias individuales) */}
        <div className="mt-6 border-t border-border pt-6">
          <h2 className="mb-3 text-base font-semibold text-foreground">
            Funnel de cartera <span className="text-sm font-normal text-muted-foreground">— vista agregada</span>
          </h2>
          <CarteraFunnelSection />
        </div>
      </div>
    </div>
  );
}
